// Comprehensive FAQ data, updated to reflect current platform features.

import { PREMIUM_TRIAL_DAYS, PRICING } from '@/lib/auth/feature-flags'
import { getActiveTools } from '@/data/tools-manifest'

const ACTIVE_TOOL_COUNT = getActiveTools().length

export interface FAQItem {
  id: string
  question: string
  answer: string
  category: string
  order: number
}

export const comprehensiveFaqData: FAQItem[] = [
  // ===================================
  // ABOUT WHAT GRASS IS THIS? (NEW)
  // ===================================
  {
    id: "platform-overview",
    question: "What is What Grass Is This?",
    answer: `What Grass Is This? is a lawn care platform that helps you identify your grass type from photos, get personalized care plans, and access ${ACTIVE_TOOL_COUNT} free lawn care tools. Photo analysis returns a grass type, confidence score, and region-specific recommendations based on your ZIP code.`,
    category: "About What Grass Is This?",
    order: 1
  },
  {
    id: "how-platform-works",
    question: "How does the grass identification work?",
    answer: "Upload a close-up photo of your grass blades (6-12 inches away), and our system analyzes the blade shape, color, texture, and growth pattern to identify your grass type. Within seconds, you'll get your grass type, a confidence score, and a personalized 12-month care plan based on your location.",
    category: "About What Grass Is This?",
    order: 2
  },
  {
    id: "what-makes-different",
    question: "How is What Grass Is This? different from other lawn care sites?",
    answer: `Unlike generic lawn care guides, we combine photo-based grass identification with location-based recommendations and ${ACTIVE_TOOL_COUNT} specialized tools. You get personalized care plans tailored to your exact grass type and ZIP code, plus tools to calculate fertilizer amounts, watering schedules, seeding timing, and more, all in one platform.`,
    category: "About What Grass Is This?",
    order: 3
  },
  {
    id: "grass-types-supported",
    question: "What grass types can you identify?",
    answer: "We identify all major grass types including Kentucky Bluegrass, Bermudagrass, Tall Fescue, Fine Fescue, Perennial Ryegrass, Zoysiagrass, St. Augustinegrass, Centipede, Bahiagrass, and Buffalograss. Our system works with warm-season, cool-season, and transition-zone grasses across the United States.",
    category: "About What Grass Is This?",
    order: 4
  },
  {
    id: "mobile-app-availability",
    question: "Is there a mobile app?",
    answer: "Yes. The What Grass Is This? iPhone app is available now in the App Store. Use Get the app in the site navigation to install it. The mobile website also works in any modern phone browser, and an Android app is still in development.",
    category: "About What Grass Is This?",
    order: 5
  },

  // ===================================
  // PRICING & PREMIUM (NEW)
  // ===================================
  {
    id: "is-it-free",
    question: "Is What Grass Is This? free?",
    answer: `Yes. A free account includes 3 grass IDs plus 3 shared weed IDs or lawn diagnoses per month, 1 care plan per month with PDF download, unlimited saved lawn profiles, and access to all ${ACTIVE_TOOL_COUNT} tools. The Soil Test Analyzer includes 2 successful PDF parses per month; the other calculators remain free to use. Premium (${PRICING.MONTHLY.display} or ${PRICING.YEARLY.display}) adds unlimited scans and care plans, smart reminders, full analysis history, and saved soil-test history.`,
    category: "Pricing & Premium",
    order: 6
  },
  {
    id: "free-tier-limits",
    question: "What are the limits on the free plan?",
    answer: `Free accounts get 3 grass IDs, a shared allowance of 3 weed IDs or lawn diagnoses, and 1 care plan with PDF download per month. Saved lawn profiles are unlimited, and all ${ACTIVE_TOOL_COUNT} tools are available. The Soil Test Analyzer includes 2 successful PDF parses per month; the other calculators remain free to use. You also get 30 days of analysis history plus access to guides and blog content. Monthly limits reset on the 1st.`,
    category: "Pricing & Premium",
    order: 7
  },
  {
    id: "premium-features",
    question: "What's included in Premium?",
    answer: `Premium (${PRICING.MONTHLY.display} or ${PRICING.YEARLY.display}) includes unlimited grass IDs, weed IDs, lawn diagnoses, and care plans, smart email and push reminders for lawn tasks, full lawn health and analysis history, and saved soil tests with annual re-test reminders. PDF care plan downloads are included with every signed-in plan.`,
    category: "Pricing & Premium",
    order: 8
  },
  {
    id: "premium-pricing",
    question: "How much does Premium cost?",
    answer: `Premium is ${PRICING.YEARLY.display} or ${PRICING.MONTHLY.display}, with a ${PREMIUM_TRIAL_DAYS}-day trial for new subscribers on either plan. All plans include every premium feature with no hidden fees. Cancel anytime, no long-term commitment.`,
    category: "Pricing & Premium",
    order: 9
  },
  {
    id: "premium-worth-it",
    question: "Is Premium worth it for casual lawn owners?",
    answer: "Premium is most useful if you identify grass or weeds, diagnose lawn problems often, want seasonal reminders, or track changes with photos over time. Unlimited scans and care plans support frequent use. If you only need an occasional ID, diagnosis, or calculator, the free plan is usually enough.",
    category: "Pricing & Premium",
    order: 10
  },
  {
    id: "cancel-subscription",
    question: "Can I cancel my Premium subscription anytime?",
    answer: "Yes, cancel anytime from your account settings. You'll retain Premium features until the end of your billing period. After cancellation, you'll revert to the free plan but keep all saved lawns and analysis history.",
    category: "Pricing & Premium",
    order: 11
  },

  // ===================================
  // TOOLS & CALCULATORS (NEW)
  // ===================================
  {
    id: "available-calculators",
    question: "What lawn care calculators are available?",
    answer: `We offer ${ACTIVE_TOOL_COUNT} free tools. Popular choices include the Lawn Size Calculator, Fertilizer Calculator, Watering Schedule Generator, Soil Temperature Tracker, Seeding Calculator, Soil Test Analyzer, Lawn Cost Estimator, and Grass Comparison Tool. Open the Tools directory to search or browse the full list.`,
    category: "Tools & Calculators",
    order: 12
  },
  {
    id: "calculator-order",
    question: "Which calculator should I use first?",
    answer: "Start with the Lawn Size Calculator to measure your lawn accurately using satellite maps or manual entry. This foundational measurement is essential for getting accurate results from the Fertilizer Calculator, Seeding Calculator, and Cost Estimator.",
    category: "Tools & Calculators",
    order: 13
  },
  {
    id: "calculator-accuracy",
    question: "How accurate are the calculator recommendations?",
    answer: "Our calculators use university extension guidelines and industry best practices. Results are accurate estimates based on general recommendations. For best results, consider a professional soil test and always follow product label instructions and local regulations.",
    category: "Tools & Calculators",
    order: 14
  },
  {
    id: "calculator-account-required",
    question: "Do I need an account to use the calculators?",
    answer: "No! All calculators are free to use without registration. However, creating a free account lets you save your lawn profile (grass type, size, ZIP code) which automatically pre-fills calculator forms, saving you time.",
    category: "Tools & Calculators",
    order: 15
  },
  {
    id: "embed-calculators",
    question: "Can I embed these calculators on my website?",
    answer: "Yes! Tools marked with the 'Embeddable' badge can be added to your website for free. Click the 'Embed' button on any calculator to get the iframe code. No attribution required. Use them freely on WordPress, Wix, Squarespace, or custom sites.",
    category: "Tools & Calculators",
    order: 16
  },
  {
    id: "calculator-usage-frequency",
    question: "How often should I use these calculators?",
    answer: "Use the Lawn Size Calculator once (unless you add new lawn areas). Seasonal tools like Fertilizer and Seeding Calculators should be used 2-4 times per year based on your grass type. The Watering Schedule Generator is helpful to check throughout the growing season as weather changes.",
    category: "Tools & Calculators",
    order: 17
  },

  // ===================================
  // USING THE GRASS IDENTIFIER (NEW)
  // ===================================
  {
    id: "good-grass-photo",
    question: "What makes a good grass photo for identification?",
    answer: "Take close-up photos 6-12 inches from the grass in good natural lighting (avoid shadows or direct harsh sun). Capture individual grass blades clearly. Avoid photos where grass is too far away or blurry. Multiple angles help: top-down and side views showing blade shape and texture.",
    category: "Using the Grass Identifier",
    order: 18
  },
  {
    id: "low-confidence-score",
    question: "Why is my grass identification confidence score low?",
    answer: "A score below 50% usually means the photo is too far away, blurry, poorly lit, dormant, stressed, or mixed. Scores from 50% to 69% are shown as a Good Match with alternatives to compare. Try retaking the photo closer, in better light, and showing healthy green blades clearly.",
    category: "Using the Grass Identifier",
    order: 19
  },
  {
    id: "multiple-photo-upload",
    question: "Can I upload multiple photos of my lawn?",
    answer: "Not in a single scan today. Upload one focused photo per scan, then run another scan if you want to check a different angle. Premium removes the monthly scan limit, but each scan still uses one photo.",
    category: "Using the Grass Identifier",
    order: 20
  },
  {
    id: "disagree-with-identification",
    question: "What if I disagree with the grass identification?",
    answer: "If the confidence score is high (85%+) but you're certain it's wrong, try uploading a clearer photo. If confidence is low, check the alternative suggestions. They might match your grass better. You can also manually select your grass type in your lawn profile to override the system's recommendation.",
    category: "Using the Grass Identifier",
    order: 21
  },
  {
    id: "photo-file-requirements",
    question: "What photo formats and sizes are supported?",
    answer: "JPEG, PNG, WebP, HEIC, and HEIF are accepted up to 25MB before compression. The site compresses supported photos before upload, so most phone-camera files work without any manual resizing.",
    category: "Using the Grass Identifier",
    order: 22
  },
  {
    id: "identification-not-working",
    question: "Why isn't my grass being identified?",
    answer: "Common issues include a photo over the 25MB pre-compression limit, blocked browser camera permissions, a poor internet connection, or a server timeout. Check the file size and browser permissions, switch to a stable connection, or retry in a few minutes. Contact support if issues persist.",
    category: "Using the Grass Identifier",
    order: 23
  },

  // ===================================
  // COMMUNITY
  // ===================================
  {
    id: "share-lawn-photos",
    question: "Can I share my lawn photos with the community?",
    answer: "Yes. Open Community and choose Share your lawn. You do not need an account. Add a photo, title, ZIP code, project type, photo month, timeframe, starting condition, the work you did, and the outcome. Grass type, email, and social credit are optional. Every submission is reviewed before it appears publicly.",
    category: "Community",
    order: 24
  },
  {
    id: "community-leaderboard",
    question: "What is the community leaderboard?",
    answer: "The leaderboard currently ranks approved lawn photos by their all-time community vote score. Weekly and regional boards will open once there are enough lawns for a useful competition. You can open any ranked lawn to see its photo and details.",
    category: "Community",
    order: 25
  },
  {
    id: "vote-on-photos",
    question: "Do I need an account to vote on community photos?",
    answer: "No. You can upvote or downvote an approved photo without signing in. Selecting the same vote again removes it, and selecting the other option changes it. A browser fingerprint limits each browser to one active vote per photo.",
    category: "Community",
    order: 26
  },
  {
    id: "report-inappropriate-content",
    question: "Can I report an inappropriate community photo?",
    answer: "Yes. Use Report on the photo and choose the closest reason, such as inappropriate content, spam, wrong category, duplicate, or copyright. Reports go to the moderation queue for review.",
    category: "Community",
    order: 27
  },

  // ===================================
  // ACCOUNTS & SETTINGS (NEW)
  // ===================================
  {
    id: "why-create-account",
    question: "Why should I create an account?",
    answer: "Accounts let you save multiple lawn profiles, view analysis history, access past care plans, and auto-fill calculator forms with your lawn data. You'll also get email delivery of care plans and optional reminders for seasonal tasks (Premium feature).",
    category: "Accounts & Settings",
    order: 28
  },
  {
    id: "save-multiple-lawns",
    question: "Can I save multiple lawn profiles?",
    answer: "Yes. Signed-in users can save multiple lawn profiles on either the free or Premium plan. Each profile can store its own grass type, size, ZIP code, and lawn history, which is useful for front and back yards, rentals, or family properties.",
    category: "Accounts & Settings",
    order: 29
  },
  {
    id: "delete-account",
    question: "How do I delete my account and data?",
    answer: "Go to Settings → Account → Delete Account. This permanently removes your profile, saved lawns, analysis history, and care plans. This action cannot be undone. If you have an active Premium subscription, cancel it first to avoid future charges.",
    category: "Accounts & Settings",
    order: 30
  },
  {
    id: "data-privacy",
    question: "Is my lawn data private?",
    answer: "Your saved lawns and analysis history are private to your account, and we never sell your data. Community submissions are different: after approval, the photo, title, grass type, broad three-digit ZIP region, project timing, lawn story, and optional social handle can be public. The full five-digit ZIP is not stored with the submission, and the optional email is not shown in the public gallery. Read our Privacy Policy for full details.",
    category: "Accounts & Settings",
    order: 31
  },

  // ===================================
  // GENERAL LAWN CARE - GETTING STARTED
  // ===================================
  {
    id: "grass-type-identification-manual",
    question: "What type of grass do I have (cool-season or warm-season)?",
    answer: "Cool-season grasses thrive in northern climates and grow actively in spring and fall, while warm-season grasses love heat and are common in the South. To identify your lawn, observe its growth pattern: cool-season lawns stay green into late fall and green up early in spring, while warm-season lawns green up late and turn brown in winter.",
    category: "Getting Started",
    order: 32
  },
  {
    id: "choose-right-grass",
    question: "How do I choose the right grass type for my yard?",
    answer: "Match grass to your climate zone first (cool-season for northern regions, warm-season for southern regions). Consider sun exposure and choose shade-tolerant varieties for shady areas. Select durable, traffic-tolerant grass for high-use areas. Factor in maintenance preferences and choose varieties recommended by local extension services for your region.",
    category: "Getting Started",
    order: 33
  },
  {
    id: "warm-vs-cool-season-care",
    question: "How is caring for warm-season vs. cool-season grass different?",
    answer: "Warm-season grasses thrive in summer heat and go dormant in winter, while cool-season grasses prefer cooler temperatures and stay green in winter. Fertilize warm-season grass in late spring/summer, cool-season in fall/spring. Mow warm-season grass lower and more frequently. Warm-season grass is more drought-tolerant but turns brown in winter.",
    category: "Getting Started",
    order: 34
  },
  {
    id: "transition-zone-lawn",
    question: "I live in a transition zone, which grass should I plant?",
    answer: "Choose based on your priorities: cool-season grass stays green longer but struggles in summer heat, while warm-season grass handles summer beautifully but goes brown in winter. Tall fescue is popular in transition zones for its heat tolerance. Some homeowners overseed warm-season grass with ryegrass in fall for winter color.",
    category: "Getting Started",
    order: 35
  },

  // ===================================
  // GENERAL LAWN CARE - WATERING
  // ===================================
  {
    id: "watering-frequency",
    question: "How often and how much should I water my lawn?",
    answer: "Most lawns need about 1 inch of water per week, delivered in 1-3 sessions rather than daily. Deep, infrequent watering encourages deeper roots and makes the lawn more drought-resistant. Use our Watering Schedule Generator for personalized recommendations based on your grass type and weather.",
    category: "Watering",
    order: 36
  },
  {
    id: "watering-time",
    question: "When is the best time of day to water?",
    answer: "Water in the early morning (6am-10am) when temperatures are cooler and winds are calmer, allowing more water to soak into the soil. Morning watering also allows grass blades to dry off during the day, preventing fungal diseases.",
    category: "Watering",
    order: 37
  },
  {
    id: "sprinkler-system-vs-hose",
    question: "Do I need an in-ground sprinkler system?",
    answer: "You can water effectively with hoses and portable sprinklers. Many homeowners do this successfully. Use a timer to automate watering and move sprinklers to cover different areas. In-ground systems offer convenience and even coverage but are expensive to install and maintain. For most homeowners, portable sprinklers are cost-effective and sufficient.",
    category: "Watering",
    order: 38
  },
  {
    id: "lawn-drainage",
    question: "How can I improve lawn drainage?",
    answer: "Aerate the lawn to relieve compaction and create channels for water. Amend heavy clay soil with organic matter like compost. Grade and fill low spots, improve surface drainage with swales, and ensure downspouts direct water away from the lawn. For severe issues, consider installing French drains or dry wells.",
    category: "Watering",
    order: 39
  },

  // ===================================
  // GENERAL LAWN CARE - MOWING
  // ===================================
  {
    id: "mowing-frequency",
    question: "How often should I mow my lawn?",
    answer: "Mow about once a week during the growing season to keep your lawn healthy and prevent excessive growth. Regular mowing helps reduce weeds and encourages thicker grass growth. Use our Mowing Height Calculator for grass-specific recommendations.",
    category: "Mowing",
    order: 40
  },
  {
    id: "mowing-height",
    question: "What height should I mow my grass?",
    answer: "Follow the 'one-third rule': never remove more than one-third of the blade length in a single mow. Most grasses are healthiest around 2½-3½ inches tall, with cool-season grasses preferring the taller side and warm-season grasses often kept shorter. Check our Mowing Height Calculator for your specific grass type.",
    category: "Mowing",
    order: 41
  },
  {
    id: "grass-clippings",
    question: "Should I bag grass clippings or leave them?",
    answer: "Leave clippings on the lawn instead of bagging them. They return valuable nutrients to the soil and act as natural fertilizer. Clippings don't cause thatch buildup and decompose quickly. Only remove clippings if they form large clumps or if your lawn has an active disease.",
    category: "Mowing",
    order: 42
  },
  {
    id: "choose-lawn-mower",
    question: "What type of lawn mower should I buy?",
    answer: "For small to medium yards (under 5,000 sq ft), a push mower is budget-friendly. Self-propelled mowers are better for larger yards or hills. Riding mowers are justified for half-acre or larger lawns. Electric mowers are quieter and easier to maintain, while gas mowers have more power and longer runtime. Use our Lawn Size Calculator to determine the best mower size.",
    category: "Mowing",
    order: 43
  },
  {
    id: "sharpen-mower-blade",
    question: "How often should I sharpen my mower blade?",
    answer: "Sharpen your mower blade at least once per season, or every 20-25 hours of mowing. Replace the blade when it becomes too thin from repeated sharpening or if bent or cracked. A sharp blade cuts grass cleanly, while a dull blade tears grass, causing brown tips and making the lawn more prone to disease.",
    category: "Mowing",
    order: 44
  },

  // ===================================
  // GENERAL LAWN CARE - FERTILIZING
  // ===================================
  {
    id: "fertilizer-importance",
    question: "Is fertilizer important for my lawn?",
    answer: "Yes. Fertilizing is key to keeping grass thick, green, and healthy. Grass requires nutrients like nitrogen, phosphorus, and potassium that most soils don't naturally provide in sufficient amounts. Use our Fertilizer Calculator to determine exact amounts for your lawn size and grass type.",
    category: "Fertilizing & Soil Health",
    order: 45
  },
  {
    id: "fertilizing-timing",
    question: "When and how often should I fertilize?",
    answer: "Fertilize during your grass's active growing season. Cool-season lawns need feeding in spring and early fall, while warm-season grasses should be fertilized in late spring and summer. Generally, 2-4 feedings per year is typical. Use our Fertilizer Calculator for personalized timing recommendations.",
    category: "Fertilizing & Soil Health",
    order: 46
  },
  {
    id: "soil-testing",
    question: "Do I need to test my soil?",
    answer: "Yes, a soil test tells you the pH and nutrient levels so you can fertilize appropriately and correct imbalances. Experts recommend testing every 2-3 years. You can use a DIY kit or send samples to a local extension service.",
    category: "Fertilizing & Soil Health",
    order: 47
  },
  {
    id: "aeration",
    question: "When should I aerate my lawn?",
    answer: "Aerate during your grass's active growth period: fall for cool-season lawns and late spring for warm-season lawns. If your lawn gets heavy traffic or water puddles instead of soaking in, aeration can greatly benefit it. Use our Aeration Timing Tool for location-specific recommendations.",
    category: "Fertilizing & Soil Health",
    order: 48
  },

  // ===================================
  // GENERAL LAWN CARE - SEEDING
  // ===================================
  {
    id: "planting-timing",
    question: "When should I seed my lawn?",
    answer: "For cool-season lawns, seed in late summer to early fall. Warm-season grasses should be seeded in late spring or early summer. Avoid seeding during extreme heat or cold. Use our Seeding Calculator to check if it's too late to seed based on your location and grass type.",
    category: "Seeding & Establishment",
    order: 49
  },
  {
    id: "seed-vs-sod",
    question: "Should I use seed or sod?",
    answer: "Seed is cheaper and offers more variety but takes 2-3 months to establish and requires careful watering and protection. Sod provides instant results and faster establishment but is expensive and labor-intensive to install. Choose seed for cost-effectiveness and variety, sod for immediate impact and erosion control.",
    category: "Seeding & Establishment",
    order: 50
  },
  {
    id: "repair-bare-spots",
    question: "How do I repair bare spots in my lawn?",
    answer: "Clean the spot by removing dead grass and loosening the topsoil. Mix in compost if soil is poor, then apply grass seed that matches your existing lawn. Cover with a thin layer of straw or peat moss and water daily until new grass sprouts. For larger areas or quick results, you can patch with sod.",
    category: "Seeding & Establishment",
    order: 51
  },

  // ===================================
  // GENERAL LAWN CARE - PROBLEMS
  // ===================================
  {
    id: "weed-control",
    question: "How do I control weeds in my lawn?",
    answer: "A healthy, thick lawn is the best defense against weeds. Apply pre-emergent herbicide in early spring to prevent annual weeds like crabgrass from germinating. For existing weeds, use targeted treatments or pull them by hand. Use our Herbicide Timing Calculator for optimal application dates.",
    category: "Weeds, Pests & Problems",
    order: 52
  },
  {
    id: "lawn-pests",
    question: "How do I identify and treat lawn pests?",
    answer: "Common pests include grubs, chinch bugs, and armyworms. If you notice brown patches despite proper watering, check for pests. Grass that peels up easily may indicate grubs. Upload photos of the damage during grass identification for more specific guidance on treatment options.",
    category: "Weeds, Pests & Problems",
    order: 53
  },
  {
    id: "lawn-diseases",
    question: "How can I recognize lawn diseases?",
    answer: "Lawn diseases (mostly fungi) appear as discolored patches or spots. Common diseases include brown patch, dollar spot, red thread, and powdery mildew. A well-maintained lawn is less susceptible. If disease occurs, ensure proper watering practices and consider applying a fungicide labeled for your grass type.",
    category: "Weeds, Pests & Problems",
    order: 54
  },
  {
    id: "brown-yellow-patches",
    question: "Why do I have brown patches in my lawn?",
    answer: "Brown patches can result from drought stress, pet urine, fungal disease, grub damage, thatch buildup, or chemical burn. Identify the cause by examining the pattern and timing. Solutions include proper watering, flushing pet urine spots, improving drainage, treating for pests, dethatching, or reseeding. Upload a close-up photo during grass identification for help narrowing down the cause.",
    category: "Weeds, Pests & Problems",
    order: 55
  },
  {
    id: "dormant-vs-dead",
    question: "How can I tell if my lawn is dormant or dead?",
    answer: "Perform a tug test: dormant grass resists pulling because roots are alive, while dead grass pulls out easily. Check for uniformity: if the whole lawn turned brown uniformly after stress, it's likely dormancy. Water consistently for 10-14 days. If nothing greens up, the grass may be dead.",
    category: "Weeds, Pests & Problems",
    order: 56
  },
  {
    id: "pet-urine-spots",
    question: "How do I fix pet urine spots?",
    answer: "For fresh spots, immediately drench with water to dilute the urine. For dead spots, rake out dead grass, flush soil with water, then reseed or patch with sod. Prevent future spots by training pets to use designated areas, providing plenty of water to dilute urine, and following pets with a hose to water spots immediately.",
    category: "Weeds, Pests & Problems",
    order: 57
  },
  {
    id: "shade-lawn-care",
    question: "How do I maintain a lawn in shade?",
    answer: "Choose shade-tolerant grass varieties like fine fescues for cool-season lawns or St. Augustine for warm-season lawns. Prune tree branches to allow more sunlight, mow higher to increase leaf area for photosynthesis, water and fertilize less than sunny areas, and overseed regularly to maintain thickness.",
    category: "Weeds, Pests & Problems",
    order: 58
  },
  {
    id: "moss-control",
    question: "How do I prevent moss in my lawn?",
    answer: "Remove existing moss by raking or using moss-control products. Address underlying causes: improve drainage by aerating, adjust soil pH with lime if too acidic, increase sunlight by pruning trees, and reseed with shade-tolerant grass. Moss indicates conditions that favor it over grass, so changing those conditions prevents its return.",
    category: "Weeds, Pests & Problems",
    order: 59
  },
]

export const newCategories = [
  "About What Grass Is This?",
  "Pricing & Premium",
  "Tools & Calculators",
  "Using the Grass Identifier",
  "Community",
  "Accounts & Settings",
  "Getting Started",
  "Watering",
  "Mowing",
  "Fertilizing & Soil Health",
  "Seeding & Establishment",
  "Weeds, Pests & Problems"
]
